import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject, fromEvent, merge, timer } from 'rxjs';
import { debounceTime, takeUntil, tap } from 'rxjs/operators';
import { 
  InactivityConfig, 
  ActivityEvent, 
  SessionWarningData, 
  IInactivityService,
  SessionEventType 
} from '../interfaces/auth.interfaces';

/**
 * Servicio para detectar inactividad del usuario y manejar advertencias de sesión
 * Siguiendo principios SOLID:
 * - Single Responsibility: Solo maneja detección de actividad/inactividad
 * - Open/Closed: Extensible para nuevos tipos de eventos
 * - Dependency Inversion: Depende de abstracciones (Observable, etc.)
 */
@Injectable({
  providedIn: 'root'
})
export class InactivityService implements IInactivityService, OnDestroy {
  
  // ==================== CONFIGURACIÓN POR DEFECTO ====================
  
  private readonly DEFAULT_CONFIG: InactivityConfig = {
    inactivityWarningTime: 715, // advertencia 5 min antes del cierre (sesión 12h)
    maxInactivityTime: 720, // 12 horas total antes de cerrar sesión
    warningCountdownTime: 300, // 5 minutos de countdown en la advertencia
    monitoredEvents: [
      'mousedown', 'mousemove', 'keypress', 'scroll', 
      'touchstart', 'click', 'focus', 'blur'
    ]
  };

  // ==================== ESTADO PRIVADO ====================
  
  private config: InactivityConfig = { ...this.DEFAULT_CONFIG };
  private destroy$ = new Subject<void>();
  private isMonitoringActive = false;
  
  // Timers
  private inactivityTimer: any;
  private warningTimer: any;
  private warningCountdownTimer: any;
  
  // Subjects para eventos
  private activitySubject = new Subject<ActivityEvent>();
  private inactivityWarningSubject = new Subject<SessionWarningData>();
  private sessionExpiredSubject = new Subject<void>();
  
  // Estados
  private lastActivityTime = new Date();
  private isWarningActive = false;
  private warningCountdown = 0;

  // ==================== OBSERVABLES PÚBLICOS ====================
  
  public activity$ = this.activitySubject.asObservable();
  public inactivityWarning$ = this.inactivityWarningSubject.asObservable();
  public sessionExpired$ = this.sessionExpiredSubject.asObservable();

  constructor(private ngZone: NgZone) {
    console.log('InactivityService inicializado con configuración:', this.config);
  }

  // ==================== MÉTODOS PÚBLICOS ====================

  startMonitoring(): void {
    if (this.isMonitoringActive) {
      console.log('InactivityService ya está monitoreando');
      return;
    }

    console.log('Iniciando monitoreo de actividad del usuario...');
    this.isMonitoringActive = true;
    this.lastActivityTime = new Date();
    this.isWarningActive = false;

    // Configurar listeners de eventos
    this.setupEventListeners();
    
    // Iniciar timer de inactividad
    this.startInactivityTimer();
  }

  stopMonitoring(): void {
    if (!this.isMonitoringActive) {
      return;
    }

    console.log('Deteniendo monitoreo de actividad del usuario...');
    this.isMonitoringActive = false;
    this.isWarningActive = false;

    // Limpiar todos los timers
    this.clearAllTimers();
    
    // Notificar destrucción a todos los observables
    this.destroy$.next();
  }

  resetInactivityTimer(): void {
    if (!this.isMonitoringActive) {
      return;
    }

    const now = new Date();
    this.lastActivityTime = now;

    // Si hay una advertencia activa, ocultarla
    if (this.isWarningActive) {
      this.hideWarning();
    }

    // Reiniciar timer de inactividad
    this.startInactivityTimer();

    // Emitir evento de actividad
    this.activitySubject.next({
      type: 'activity_detected',
      timestamp: now,
      path: window.location.pathname
    });

    console.log('Timer de inactividad reiniciado:', now.toISOString());
  }

  isMonitoring(): boolean {
    return this.isMonitoringActive;
  }

  updateConfig(newConfig: Partial<InactivityConfig>): void {
    this.config = { ...this.config, ...newConfig };
    console.log('Configuración de inactividad actualizada:', this.config);

    // Si está monitoreando, reiniciar con nueva configuración
    if (this.isMonitoringActive) {
      this.stopMonitoring();
      this.startMonitoring();
    }
  }

  getConfig(): InactivityConfig {
    return { ...this.config };
  }

  // Métodos de callback (implementando interfaz)
  onInactivityWarning(callback: (data: SessionWarningData) => void): void {
    this.inactivityWarning$.subscribe(callback);
  }

  onSessionExpired(callback: () => void): void {
    this.sessionExpired$.subscribe(callback);
  }

  onActivityDetected(callback: (event: ActivityEvent) => void): void {
    this.activity$.subscribe(callback);
  }

  // ==================== MÉTODOS PRIVADOS ====================

  private setupEventListeners(): void {
    // Crear observable que combine todos los eventos monitoreados
    const events = this.config.monitoredEvents.map(eventName => 
      fromEvent(document, eventName)
    );

    // Ejecutar fuera de Angular zone para mejor rendimiento
    this.ngZone.runOutsideAngular(() => {
      merge(...events)
        .pipe(
          debounceTime(1000), // Debounce para evitar muchos eventos
          takeUntil(this.destroy$),
          tap(() => {
            // Volver a entrar en Angular zone para actualizaciones
            this.ngZone.run(() => {
              this.resetInactivityTimer();
            });
          })
        )
        .subscribe();
    });
  }

  private startInactivityTimer(): void {
    this.clearAllTimers();

    const warningTimeMs = this.config.inactivityWarningTime * 60 * 1000;

    this.inactivityTimer = setTimeout(() => {
      this.showInactivityWarning();
    }, warningTimeMs);

    console.log(`Timer de inactividad iniciado: ${this.config.inactivityWarningTime} minutos`);
  }

  private showInactivityWarning(): void {
    if (this.isWarningActive) {
      return; // Ya hay una advertencia activa
    }

    console.log('Mostrando advertencia de inactividad');
    this.isWarningActive = true;
    this.warningCountdown = this.config.warningCountdownTime;

    // Emitir advertencia inicial
    this.emitWarningData();

    // Iniciar countdown
    this.startWarningCountdown();

    // Configurar timer para expirar sesión
    const sessionExpireTimeMs = this.config.warningCountdownTime * 1000;
    this.warningTimer = setTimeout(() => {
      this.expireSession();
    }, sessionExpireTimeMs);
  }

  private startWarningCountdown(): void {
    this.warningCountdownTimer = setInterval(() => {
      this.warningCountdown--;
      this.emitWarningData();

      if (this.warningCountdown <= 0) {
        clearInterval(this.warningCountdownTimer);
      }
    }, 1000);
  }

  private emitWarningData(): void {
    const data: SessionWarningData = {
      remainingSeconds: this.warningCountdown,
      isSessionExpiring: true,
      canExtendSession: true,
      message: `Su sesión expirará en ${this.formatTime(this.warningCountdown)} por inactividad. ¿Desea continuar?`
    };

    this.inactivityWarningSubject.next(data);
  }

  private hideWarning(): void {
    if (!this.isWarningActive) {
      return;
    }

    console.log('Ocultando advertencia de inactividad');
    this.isWarningActive = false;
    
    clearTimeout(this.warningTimer);
    clearInterval(this.warningCountdownTimer);

    // Emitir datos para ocultar warning
    const data: SessionWarningData = {
      remainingSeconds: 0,
      isSessionExpiring: false,
      canExtendSession: false,
      message: ''
    };

    this.inactivityWarningSubject.next(data);
  }

  private expireSession(): void {
    console.log('Sesión expirada por inactividad');
    this.isWarningActive = false;
    this.clearAllTimers();
    this.sessionExpiredSubject.next();
  }

  private clearAllTimers(): void {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }

    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
      this.warningTimer = null;
    }

    if (this.warningCountdownTimer) {
      clearInterval(this.warningCountdownTimer);
      this.warningCountdownTimer = null;
    }
  }

  private formatTime(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    
    if (minutes > 0) {
      return `${minutes}:${secs.toString().padStart(2, '0')} minutos`;
    } else {
      return `${secs} segundos`;
    }
  }

  // ==================== CLEANUP ====================

  ngOnDestroy(): void {
    console.log('InactivityService destruido');
    this.stopMonitoring();
    this.destroy$.complete();
  }

  // ==================== MÉTODOS UTILITARIOS PÚBLICOS ====================

  /**
   * Obtiene el tiempo transcurrido desde la última actividad
   */
  getTimeSinceLastActivity(): number {
    return Date.now() - this.lastActivityTime.getTime();
  }

  /**
   * Verifica si el usuario está actualmente activo
   */
  isUserActive(): boolean {
    const timeSinceActivity = this.getTimeSinceLastActivity();
    const thresholdMs = this.config.inactivityWarningTime * 60 * 1000;
    return timeSinceActivity < thresholdMs;
  }

  /**
   * Fuerza la extensión de sesión (útil para botones de "Continuar sesión")
   */
  extendSession(): void {
    console.log('Extensión manual de sesión solicitada');
    this.resetInactivityTimer();
  }

  /**
   * Obtiene estadísticas de actividad
   */
  getActivityStats(): {
    lastActivity: Date;
    timeSinceLastActivity: number;
    isActive: boolean;
    isMonitoring: boolean;
    isWarningActive: boolean;
  } {
    return {
      lastActivity: this.lastActivityTime,
      timeSinceLastActivity: this.getTimeSinceLastActivity(),
      isActive: this.isUserActive(),
      isMonitoring: this.isMonitoringActive,
      isWarningActive: this.isWarningActive
    };
  }
}
